import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { Link } from "wouter";
import { ArrowLeft, TrendingUp, TrendingDown, Filter } from "lucide-react";

// Mock transaction data - replace with real data from API
const mockTransactions = [
  {
    id: 1,
    type: "earn",
    amount: 500,
    description: "Top-up: Free Fire 50 Diamonds",
    date: new Date(2026, 3, 15, 14, 30),
    reference: "TXN-001",
  },
  {
    id: 2,
    type: "burn",
    amount: 50,
    description: "Discount applied on Free Fire top-up",
    date: new Date(2026, 3, 15, 14, 25),
    reference: "TXN-002",
  },
  {
    id: 3,
    type: "earn",
    amount: 1000,
    description: "Promo: Spring Bonus",
    date: new Date(2026, 3, 14, 10, 15),
    reference: "TXN-003",
  },
  {
    id: 4,
    type: "burn",
    amount: 200,
    description: "Discount applied on PUBG Mobile top-up",
    date: new Date(2026, 3, 13, 16, 45),
    reference: "TXN-004",
  },
  {
    id: 5,
    type: "earn",
    amount: 750,
    description: "Top-up: PUBG Mobile 100 UC",
    date: new Date(2026, 3, 12, 9, 20),
    reference: "TXN-005",
  },
  {
    id: 6,
    type: "burn",
    amount: 100,
    description: "Discount applied on Genshin Impact top-up",
    date: new Date(2026, 3, 11, 13, 0),
    reference: "TXN-006",
  },
  {
    id: 7,
    type: "earn",
    amount: 300,
    description: "Top-up: Genshin Impact 60 Primogems",
    date: new Date(2026, 3, 10, 11, 30),
    reference: "TXN-007",
  },
];

type FilterType = "all" | "earn" | "burn";

export default function PlayXpointHistory() {
  const [filterType, setFilterType] = useState<FilterType>("all");

  const filteredTransactions = useMemo(() => {
    if (filterType === "all") return mockTransactions;
    return mockTransactions.filter((t) => t.type === filterType);
  }, [filterType]);

  const totalEarned = mockTransactions
    .filter((t) => t.type === "earn")
    .reduce((sum, t) => sum + t.amount, 0);

  const totalBurned = mockTransactions
    .filter((t) => t.type === "burn")
    .reduce((sum, t) => sum + t.amount, 0);

  const netBalance = totalEarned - totalBurned;

  return (
    <Layout>
      <div className="min-h-screen bg-white text-black py-8 md:py-12">
        <div className="container">
          {/* Header */}
          <div className="mb-8">
            <Link href="/" className="flex items-center gap-2 text-black/60 hover:text-black mb-4">
              <ArrowLeft size={18} />
              <span className="font-mono text-sm">Back to Home</span>
            </Link>
            <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">
              PLAYXPOINT HISTORY
            </h1>
            <p className="text-black/60 mt-2 font-mono text-sm">
              Track your PlayXpoint earn and burn transactions
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Total Earned */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0 }}
              className="border-3 border-black p-6 bg-white"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs tracking-widest text-black/50">TOTAL EARNED</span>
                <TrendingUp size={20} className="text-green-600" />
              </div>
              <div className="font-heading font-bold text-3xl">{totalEarned.toLocaleString()}</div>
              <div className="text-black/50 font-mono text-xs mt-2">Points</div>
            </motion.div>

            {/* Total Burned */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="border-3 border-black p-6 bg-white"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs tracking-widest text-black/50">TOTAL BURNED</span>
                <TrendingDown size={20} className="text-red-600" />
              </div>
              <div className="font-heading font-bold text-3xl">{totalBurned.toLocaleString()}</div>
              <div className="text-black/50 font-mono text-xs mt-2">Points</div>
            </motion.div>

            {/* Net Balance */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="border-3 border-black p-6 bg-black text-white"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs tracking-widest text-white/50">NET BALANCE</span>
                <Filter size={20} />
              </div>
              <div className="font-heading font-bold text-3xl">{netBalance.toLocaleString()}</div>
              <div className="text-white/50 font-mono text-xs mt-2">Points</div>
            </motion.div>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-3 mb-8 border-b-3 border-black pb-6">
            <button
              onClick={() => setFilterType("all")}
              className={`font-mono text-xs tracking-widest px-4 py-2 border-2 transition-all ${
                filterType === "all"
                  ? "bg-black text-white border-black"
                  : "border-black hover:bg-black/5"
              }`}
            >
              ALL
            </button>
            <button
              onClick={() => setFilterType("earn")}
              className={`font-mono text-xs tracking-widest px-4 py-2 border-2 transition-all ${
                filterType === "earn"
                  ? "bg-green-600 text-white border-green-600"
                  : "border-black hover:bg-black/5"
              }`}
            >
              EARNED
            </button>
            <button
              onClick={() => setFilterType("burn")}
              className={`font-mono text-xs tracking-widest px-4 py-2 border-2 transition-all ${
                filterType === "burn"
                  ? "bg-red-600 text-white border-red-600"
                  : "border-black hover:bg-black/5"
              }`}
            >
              BURNED
            </button>
          </div>

          {/* Transactions Table */}
          <div className="border-3 border-black overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-black text-white border-b-3 border-black">
                    <th className="px-4 py-3 text-left font-mono text-xs tracking-widest">DATE</th>
                    <th className="px-4 py-3 text-left font-mono text-xs tracking-widest">TYPE</th>
                    <th className="px-4 py-3 text-left font-mono text-xs tracking-widest">
                      DESCRIPTION
                    </th>
                    <th className="px-4 py-3 text-right font-mono text-xs tracking-widest">AMOUNT</th>
                    <th className="px-4 py-3 text-left font-mono text-xs tracking-widest">
                      REFERENCE
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((tx, i) => (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: i * 0.05 }}
                        className="border-b border-black/10 hover:bg-black/5 transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-xs">
                          {tx.date.toLocaleDateString("th-TH", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })}{" "}
                          {tx.date.toLocaleTimeString("th-TH", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`font-mono text-xs font-bold px-2 py-1 border-2 ${
                              tx.type === "earn"
                                ? "border-green-600 text-green-600"
                                : "border-red-600 text-red-600"
                            }`}
                          >
                            {tx.type === "earn" ? "EARN" : "BURN"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{tx.description}</td>
                        <td
                          className={`px-4 py-3 text-right font-heading font-bold ${
                            tx.type === "earn" ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {tx.type === "earn" ? "+" : "-"}
                          {tx.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-black/50">{tx.reference}</td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-black/50">
                        <span className="font-mono text-sm">No transactions found</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-8 p-4 bg-black/5 border-2 border-black">
            <p className="font-mono text-xs text-black/60">
              💡 PlayXpoint is earned from game top-ups and promotional campaigns. You can use your
              PlayXpoint balance to get discounts on future top-ups.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
